import { SecureTokenManager } from "../auth/secureTokenManager";

export const REMOTE_CONFIG_PATH = "https://config.oculera.site/oculera/config.json";

export let timezoneLongString = Intl.DateTimeFormat().resolvedOptions().timeZone;
if (!timezoneLongString || timezoneLongString === "") {
  timezoneLongString = "Europe/Istanbul";
}
/* Version Constants */
export const SCENE_UPDATE_MESSAGES = "2.3";
export const VOICE_ASSISTANT = "2.6";
export const NEW_PATTERNS = "2.6.2";
export const NEW_LANGUAGES = "2.6.3";
export const NEW_LANGUAGES_2 = "2.6.4";
export const NEW_LANGUAGES_3 = "2.6.6";
export const FOVEA = "2.6.5";
export const USB_UPDATE_NEEDED = "2.6.7";
export const NEW_TESTS1 = "2.7";
export const NEW_TESTS2 = "2.7.1";
export const NEW_TESTS3 = "2.7.4";
export const NEW_TESTS4 = "2.7.8";
export const NEW_TESTS5 = "2.7.10"; // Tumbling E added
export const MIN_VIRTUAL_ASSISTANT_VERSION = "2.7.9";
export const FIXATION_HANDLING_UPDATE = "2.7.9";
export const COLOR_VISION_SINGLE_EYE_TEST_VERSION = "2.7.11"; // Left or Right Eye Option Added for Color Vision Test
export const AMSLER_GRID_SINGLE_EYE_TEST_VERSION = "2.7.11"; // Left or Right Eye Option Added for Amsler Grid Test

const DEFAULT_ICE_SERVERS = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun1.l.google.com:19302" },
];

const ICE_SERVERS_CACHE_TTL_MS = 60 * 60 * 1000;
let iceServersCache = null;
let iceServersCacheExpiresAt = 0;
let iceServersFetchPromise = null;

export class Constants {
  REST_API_URL = "http://localhost:4000";
  WEBSOCKET_API_URL = "ws://localhost:4000";
  DICOM_SERVER_URL = "http://localhost:5000";
  DICOM_UID_ROOT = "";
  static instance = null;

  static async construct() {
    if (Constants.instance === null) {
      if (process.env.REACT_APP_OFFLINE !== "true") {
        try {
          const response = await fetch(REMOTE_CONFIG_PATH);
          const data = await response.json();
          const versionData = data[process.env.REACT_APP_CONFIG_KEY];
          const dicomData = data["dicom"];
          console.log("data", data, versionData);
          Constants.instance = new Constants(versionData, dicomData);
        } catch (error) {
          console.log("Failed to fetch remote config, using defaults:", error);
          Constants.instance = new Constants();
        }
      } else {
        Constants.instance = new Constants();
      }
    }
    console.log("Constants constructed", Constants.instance);
    return Constants.instance;
  }

  constructor(versionData = null, dicomData = null) {
    console.log("Constants constructor called env", process.env);
    if (process.env.REACT_APP_USE_LOCAL_SERVERS === "true") {
      console.log("using local servers");
      const localApiOrigin = resolveLocalApiOrigin();
      this.REST_API_URL = localApiOrigin;
      this.WEBSOCKET_API_URL = localApiOrigin.replace(/^http/, "ws");
      this.DICOM_SERVER_URL = "http://localhost:5000";
      this.DICOM_UID_ROOT = process.env.REACT_APP_DICOM_UID_ROOT ?? "";
      Constants.instance = this;
      return;
    }

    console.log("fetching remote config");

    console.log("remote config fetched");
    if (versionData) {
      this.REST_API_URL = versionData["restApiUrl"];
      this.WEBSOCKET_API_URL = versionData["webSocketApiUrl"];
      this.DICOM_SERVER_URL = dicomData["url"];
      this.DICOM_UID_ROOT = dicomData["uidRoot"] ?? "";
    }
  }
}

/**
 * Must derive from the page origin, not a fixed localhost: the system is reached
 * by hostname, by IP, and from the machine itself, and a hardcoded localhost would
 * send every request from a tablet back to that tablet.
 * REACT_APP_LOCAL_API_ORIGIN overrides it for development.
 */
function resolveLocalApiOrigin() {
  const override = process.env.REACT_APP_LOCAL_API_ORIGIN;
  if (override) {
    return override.replace(/\/+$/, "");
  }
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return "http://localhost:4000";
}

export function fetchIceServers() {
  const now = Date.now();
  if (iceServersCache && now < iceServersCacheExpiresAt) {
    return Promise.resolve(iceServersCache);
  }
  if (iceServersFetchPromise) {
    return iceServersFetchPromise;
  }
  iceServersFetchPromise = (async () => {
    const baseUrl = Constants.instance?.REST_API_URL;
    if (!baseUrl) {
      return DEFAULT_ICE_SERVERS;
    }

    try {
      const token = SecureTokenManager.getAccessToken?.();
      const response = await fetch(`${baseUrl}/webrtc/ice-servers`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      if (!Array.isArray(data) || data.length === 0) {
        throw new Error("Empty iceServers response");
      }
      iceServersCache = data;
      iceServersCacheExpiresAt = Date.now() + ICE_SERVERS_CACHE_TTL_MS;
      return data;
    } catch (error) {
      console.warn("Failed to fetch ICE servers from backend, falling back to STUN only:", error);
      return DEFAULT_ICE_SERVERS;
    } finally {
      iceServersFetchPromise = null;
    }
  })();
  return iceServersFetchPromise;
}

/* REST API PATHS */
export const SIGN_IN_PATH = "/auth/sign-in";
export const VERIFY_LOGIN = "/auth/verify-login";
export const MFA_PATH = "/auth/verify-2fa";
export const FORGOT_PASS_PATH = "/auth/forgot-password";
export const RESET_PASS_PATH = "/auth/reset-password";
export const USER_RESET_PASSWORD_PATH = "/auth/user/reset-password";
export const REFRESH_TOKEN_PATH = "/auth/refresh-if-needed";
export const MFA_ENABLE_PATH = "/org/enable2fa";
export const MFA_ENABLE_VERIFY_PATH = "/org/verify2faEnable";
export const CHANGE_PASS_PATH = "/auth/change-password";
export const CONTACT_FORM_PATH = "/support/send-support-request";
export const HELP_FORM_PATH = "/support/send-help-request";
export const HELP_REQUESTS_PATH = "/support/my-tickets";
export const TEST_PATH = "/test";
export const CPT_EXCEL_EXPORT_JOB_PATH = "/test/export/cpt-excel/jobs";
export const RAW_EXPORT_JOB_PATH = "/test/export/jobs";
export const TEST_LIST_PATH = "/test/list";
export const TEST_EXISTS_PATH = "/test/exists";
export const CPT_EXCEL_EXPORT_PATH = "/test/export/cpt-excel";
export const PATIENT_PATH = "/patient";
export const PATIENT_BULK_INSERT_PATH = "/patient/bulk-insert";
export const PATIENT_PAGINATED_PATH = "/patient/paginated";
export const TRANSLATIONS_PATH = "/translations";
export const TRANSLATIONS_PAGINATED_PATH = "/translations/paginated";
export const ORG_PATH = "/org";
export const ORG_UPLOAD_LOGO_PATH = "/org/upload-logo";
export const ORG_TOGGLE_ACTIVE_PATH = "/org/toggle-active";
export const ORG_DIST_UPDATE_PATH = "org/dist-update";
export const ORG_GET_PRODUCTS = "/org/get-products";
export const ORG_GET_PRODUCTS_DETAILS = "/org/get-products-details";
export const ORG_GET_PRODUCTS_VERSIONS = "/org/get-products-versions";
export const ORG_PID = "/org/org-pid";
export const ORG_VERSION_DISTRIBUTION_PATH = "/org/version-distribution";
export const ORG_SCOPE_DISTRIBUTION_PATH = "/org/scope-distribution";
export const NORMAL_VALUES_PATH = "/normal";
export const USER_PATH = "/auth/user";
export const CHATBASE_IDENTITY_PATH = "/auth/chatbase-identity";
export const TEST_CONF_PATH = "/pos/conf";
export const PRODUCT_PATH = "/product";
export const PRODUCT_PAGINATED_PATH = "/product/list";
export const ID_PATH = "/reader";
export const LOG_PATH = "/log";
export const DEVICE_LOG_PATH = "/log/device";
export const SYSTEM_LOG_PATH = "/log/system";
export const ALIAS_PATH = "/org/add-alias-to-product";
export const DIST_ALIAS_PATH = "/org/add-alias-to-product-dist";
export const DIST_GET_ORGS = "/org/orgs-of-distributor";
export const ORG_LIST_PATH = "/org/list";
export const DIST_GET_USERS = "/org/users-of-org";
export const DIST_GET_REQS = "/org/get-org-account-requests";
export const DIST_CREATE_ORG_REQ = "/org/create-org-account-request";
export const ORG_CHECK_USERNAME_PATH = "/org/check-username-availability";
export const DIST_APPROVE_ORG_REQ = "/org/approve-org-account-request";
export const DIST_DELETE_ORG_REQ = "/org/delete-org-account-requests";
export const ADMIN_DIST_ORG = "/dist/get-dist-orgs";
export const SYNC_PATH = "/sync-info/initiate-sync";
export const SYNC_CHECK_PATH = "/sync-info/check-sync";
export const SYNC_STATUS_PATH = "/sync-info/sync-status";
export const WS_PATH = "/ws";
export const TURN_OFF_DEVICE_PATH = "/ws/turn-off";
export const UPDATE_PLAYERPREF_PATH = "/ws/update-playerpref";
export const UPDATE_DEVICE_CONFIG_PATH = "/ws/update-config";
export const RESET_DEVICE_CONFIG_PATH = "/ws/reset-config";
export const WS_GET_CONFIG_PATH = "/ws/config";
export const ORG_CREATE_OFFLINE_REQUEST_PATH = "/org/create-offline-license-request";
export const ORG_GET_OFFLINE_REQUESTS_PATH = "/org/get-offline-license-requests";
export const ORG_USERS_LICENSE_INFO_PATH = "/org/users-license-info";
export const ORG_USERS_PATH = "/org/users";
export const ORG_GET_CREATED_SUBMISSIONS_PATH = "/org/submissions";
export const ORG_CANCEL_SUBMISSION_PATH = "/org/cancel-submission";
export const ORG_APPROVE_OR_DENY_SUBMISSION_PATH = "/org/approve-or-deny-submission";
export const ORG_APPROVE_OFFLINE_REQUEST_PATH = "/org/approve-offline-license-request";
export const ORG_DELETE_OFFLINE_REQUEST_PATH = "/org/delete-offline-license-request";
export const ORG_REISSUE_OFFLINE_LICENSE_PATH = "/org/reissue-offline-license";
export const DICOM_CREATE_PATH = "/create";
export const BASELINE_PATH = "/baseline-tests";
export const CAREERS_APPLY_JOB_PATH = "/career";
export const SCOPE_PATH = "/scope";
export const ASSIGN_SCOPE_PATH = "/org/add-scope-name-multiple";
export const GET_ORGS_WITH_SCOPE_PATH = "/org/get-org-ids-by-scope-name";
export const DEFAULT_CONFIG_PATH = "/product/default-config";
export const DEFAULT_CONFIG_DEVICES_PATH = "/product/default-config/devices";
export const DEFAULT_CONFIG_DEVICES_BY_VERSION_PATH = "/product/default-config/devices-by-version";
export const PRODUCT_CONFIG_PATH = "/product/config";
export const VERSION_PATH = "/versions";
export const HEADSET_RELEASES_PATH = "/headset-releases";
export const NOTIFICATIONS_ELIGIBLE_PATH = "/notifications/eligible";
export const NOTIFICATIONS_PATH = "/notifications";
export const NOTIFICATIONS_ADMIN_PATH = "/notifications/admin";
export const ADMIN_CONFIGURATIONS_PATH = "/admin-configurations";
export const DISTRIBUTOR_PATH = "/dist";
export const VIRTUAL_ASSISTANT_VIDEO_STREAM_PATH = "/updates/virtual-assistant/video";
export const VIRTUAL_ASSISTANT_VIDEO_BLOB_PATH = "/updates/virtual-assistant/video-blob";
export const VIRTUAL_ASSISTANT_VIDEO_ISSUE_REPORT_PATH =
  "/support/virtual-assistant/video-issue-report";

export const ANALYTICS_TEST_COUNT_PATH = "/analytics/test-count-by-org";
export const ANALYTICS_TEST_COUNT_BY_DEVICE_PATH = "/analytics/test-count-by-device";
export const ANALYTICS_TEST_COUNT_BY_DISTRIBUTOR_PATH = "/analytics/test-count-by-distributor";
export const ANALYTICS_DISTRIBUTOR_SALES_PATH = "/analytics/sales-by-distributor";
export const ANALYTICS_EXPORT_PATH = "/analytics/export";
export const ANALYTICS_CATEGORY_DISTRIBUTION_BY_DISTRIBUTOR_PATH =
  "/analytics/category-distribution-by-distributor";
export const ANALYTICS_ORG_BREAKDOWN_PATH = "/analytics/org-breakdown";
export const ANALYTICS_DEMO_TEST_TOTALS_PATH = "/analytics/demo-test-totals";
export const ANALYTICS_DISTRIBUTOR_COMPARISON_PATH = "/analytics/distributor-comparison";
export const ANALYTICS_MULTI_ACCOUNT_DEVICES_PATH = "/analytics/multi-account-devices";
export const ORGANIZATION_DISTRIBUTOR_ALL_IDS_PATH = "/org/distinct-distributors";
export const DISTRIBUTOR_IDS_WITH_COUNTRY_PATH = "/dist/ids-with-country";
export const ORG_CREATE_ADD_DEVICE_SUBMISSION_PATH = "/org/create-add-device-submission";
export const ORG_CREATE_REMOVE_DEVICE_SUBMISSION_PATH = "/org/create-remove-device-submission";
export const ORG_GET_ORG_FROM_PRODUCT_PATH = "/org/get-org-id-from-product";
export const ORG_ONBOARDING_ADD_DEVICE_PATH = "/org/onboarding/add-device";
export const TEST_SUITE_PATH = "/test-suite";
export const TEST_SUITE_SESSION_PATH = "/test-suite-session";

export const MANAGEMENT_ORDERS_PATH = "/management/orders";
export const MANAGEMENT_LICENCES_PATH = "/management/licenses";
export const MANAGEMENT_ASSIGN_REQUESTS_PATH = "/management/assign-requests";
export const MANAGEMENT_INVOICES_PATH = "/management/invoices";
export const MANAGEMENT_IMPORT_INVOICES_CSV_PATH = "/management/import-invoices-csv";
export const MANAGEMENT_IMPORT_INVOICES_JSON_PATH = "/management/import-invoices-json";
export const MANAGEMENT_INVOICE_SKU_TYPES_PATH = "/management/invoice-sku-types";
export const MANAGEMENT_PARENT_OFFERS_PATH = "/management/parent-offers";

/* Blog API Paths */
export const BLOG_PATH = "/blog";

/* Audit API Paths */
export const AUDIT_EVENTS_PATH = "/audit/events";

/* Offline system provisioning (offline mode only) */
export const OFFLINE_PROVISIONING_STATUS_PATH = "/offline-provisioning/status";
export const OFFLINE_PROVISIONING_ACTIVATE_WITH_FILE_PATH =
  "/offline-provisioning/activate-with-file";
